import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { differenceInDays, differenceInMinutes } from 'date-fns';
import { interval, startWith, takeUntil, map, mergeMap, EMPTY, Subject, switchMap, BehaviorSubject, take } from 'rxjs';

import { StreamAdapter, StreamCardResponse } from '@modules/content-api/adapter/stream.adapter';
import { ContentAPIService } from '@modules/content-api/content-api.service';

import { MediaStreamModel } from '../../media/models/media-stream.model';
import { VideoStatus } from '../../media/models/media-video.model';

const STREAM_PARAMS = new HttpParams().appendAll({
  include: ['player_config'].join(),
});

@Injectable({
  providedIn: 'root',
})
export class StreamService {
  private apiService = inject(ContentAPIService);
  private streamAdapter = inject(StreamAdapter);

  public getPlayerConfig(id: string) {
    return this.apiService
      .getResource('player_configs', id)
      .pipe(map((data: any) => this.streamAdapter.parsePlayerConfig(data)));
  }

  public pollPlayerStatusForStream(streamId: string, stopPoll$: Subject<boolean>) {
    // internal subject to stop the polling
    const _stopPoll$ = new Subject<boolean>();

    // get stream & player config
    return this.getStream(streamId).pipe(
      take(1),
      map((stream: MediaStreamModel | null) => {
        return {
          stream,
          pollIntervalInSeconds: this.getPollIntervalForStream(stream?.startDate, stream?.playerConfig?.status),
        };
      }),
      mergeMap(({ stream, pollIntervalInSeconds }) => {
        if (pollIntervalInSeconds > 0) {
          const pollInterval = new BehaviorSubject<number>(pollIntervalInSeconds);
          return pollInterval.pipe(
            switchMap((pollIntervalInSeconds) => {
              return interval(pollIntervalInSeconds * 1000).pipe(
                startWith(0),
                // stop polling from outside (component)
                takeUntil(stopPoll$),
                // stop polling from inside (based on status)
                takeUntil(_stopPoll$),
                mergeMap(() => {
                  if (stream?.playerConfig) {
                    return this.getPlayerConfig(stream.playerConfig.id).pipe(
                      map((playerConfig) => {
                        // check if the player status has changed, and adapt poll interval accordingly
                        const newInterval = this.getPollIntervalForStream(stream.startDate, playerConfig?.status);
                        if (newInterval != pollIntervalInSeconds) {
                          if (newInterval === 0) {
                            // stop polling
                            _stopPoll$.next(true);
                            _stopPoll$.complete();
                          } else {
                            // update poll interval
                            pollInterval.next(newInterval);
                          }
                        }
                        return playerConfig;
                      })
                    );
                  } else {
                    return EMPTY;
                  }
                })
              );
            })
          );
        }
        return EMPTY;
      })
    );
  }

  private getStream(id: string) {
    return this.apiService.getResource('streams', id, STREAM_PARAMS).pipe(
      map((data) => {
        try {
          return new MediaStreamModel(this.streamAdapter.parse(data as StreamCardResponse));
        } catch {
          return null;
        }
      })
    );
  }

  private getPollIntervalForStream(startDate?: Date, status = VideoStatus.PRE_LIVE) {
    let pollIntervalInSeconds = 0;
    if (startDate) {
      switch (status) {
        case VideoStatus.PRE_LIVE: {
          // if start date is more than a day away
          if (differenceInDays(startDate, new Date()) > 1) {
            pollIntervalInSeconds = 1 * 60 * 60; // poll once per hour
            break;
          }
          // start date is more than 5 minutes away
          if (differenceInMinutes(startDate, new Date()) > 5) {
            pollIntervalInSeconds = 2 * 60;
            break;
          } else {
            pollIntervalInSeconds = 10;
            break;
          }
        }
        case VideoStatus.LIVE: {
          pollIntervalInSeconds = 10;
          break;
        }
        case VideoStatus.STOPPED: {
          pollIntervalInSeconds = 10;
          break;
        }
        case VideoStatus.VOD:
        default: {
          // no polling
          pollIntervalInSeconds = 0;
        }
      }
    }
    return pollIntervalInSeconds;
  }
}
